/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Modal, Popconfirm, Tooltip } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import OTPInput from 'otp-input-react';
import PhoneInput from 'react-phone-input-2';
import useGenerateOtp from 'hooks/api/useGenerateOtp';
import { updateProfile } from 'apis/rest/UpdateProfile';
import { openNotification } from 'Apps/VenueBuilder/helpers/openNotification';
import { isEmpty, startsWith } from 'lodash';
import { useDispatch } from 'react-redux';
import * as actUser from 'redux/actions/common/userActions';
import CountDownTimer from 'ComponentsV2/ProjectDashboard/Components/Common/UpgradeMessage/CountDown/CountDownTimer';

const REQUEST_OTP_TIME = 1;
const inputStyles = {
    width: '45px',
    height: '40px',
    borderWidth: '1px',
    borderRadius: '0.375rem',
    marginBottom: 0,
};

const validateEmail = (email) => {
    const emailPattern = /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
    return emailPattern.test(email);
};
const validatePhoneNo = (phone) => {
    const phoneNumberRegex = /^\+?[0-9\s-]{7,15}$/;
    return phoneNumberRegex.test(phone);
};
const UserProfileEdit = ({
    user,
    showUserProfileEdit = true,
    setShowUserProfileEdit,
}) => {
    const dispatch = useDispatch();
    const [submitting, setSubmitting] = useState(false);
    const [editProfileState, setEditProfileState] = useState({
        showOTPforExistingEmail: false,
        showOTPForNewEmail: false,
        showOTPForExistingPhone: false,
        showOTPForNewPhone: false,
        showFormForAddingPhone: false,
        showFormForAddingEmail: false,
    });
    const [startCountdownForOTP, setStartCountdownForOTP] = useState({
        existingPhone: false,
        newPhone: false,
        existingEmail: false,
        newEmail: false,
    });

    const { register, handleSubmit, watch, setValue, reset } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        defaultValues: {
            firstname: user?.firstname,
            lastname: user?.lastname,
            email: user?.email,
            phoneNo: user?.phoneNo,
        },
    });

    const {
        firstname,
        lastname,
        newEmail,
        emailCode1,
        emailCode2,
        newPhoneNo,
        phoneCode1,
        phoneCode2,
    } = watch();

    const { generateOTP } = useGenerateOtp();

    const handleFormSubmit = async (data) => {
        // Now you get all the form data in a single object
        try {
            setSubmitting(true);
            const { email, phoneNo, newPhoneNo, newEmail, ...rest } = data;
            const payload = {
                ...rest,
            };
            if (newPhoneNo) payload.phoneNo = newPhoneNo;
            if (newEmail) payload.email = newEmail;

            const res = await updateProfile(payload);
            if (!res?.status) throw new Error(res?.message);
            openNotification('success', { message: res?.message });
            setEditProfileState({
                showOTPforExistingEmail: false,
                showOTPForNewEmail: false,
                showOTPForExistingPhone: false,
                showOTPForNewPhone: false,
                showFormForAddingPhone: false,
                showFormForAddingEmail: false,
            });
            const userUpdatedPayload = {
                ...user,
                ...payload,
            };
            setShowUserProfileEdit(false);
            dispatch(actUser.setUser({ user: userUpdatedPayload }));
        } catch (error) {
            openNotification('error', { message: error?.message });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = useCallback(() => {
        reset({
            firstname: user?.firstname,
            lastname: user?.lastname,
            email: user?.email,
            phoneNo: user?.phoneNo,
            newPhoneNo: '',
            newEmail: '',
            emailCode1: '',
            emailCode2: '',
            phoneCode1: '',
            phoneCode2: '',
        });
        setEditProfileState({
            showOTPforExistingEmail: false,
            showOTPForNewEmail: false,
            showOTPForExistingPhone: false,
            showOTPForNewPhone: false,
            showFormForAddingPhone: false,
            showFormForAddingEmail: false,
        });
        setShowUserProfileEdit(false);
    }, [setShowUserProfileEdit, reset, user]);

    const handleCancelChangeEmail = useCallback(() => {
        reset({
            firstname: user?.firstname,
            lastname: user?.lastname,
            email: user?.email,
            phoneNo: user?.phoneNo,
            newPhoneNo,
            phoneCode1,
            phoneCode2,
            newEmail: '',
            emailCode1: '',
            emailCode2: '',
        });
        setEditProfileState((prev) => ({
            ...prev,
            showOTPforExistingEmail: false,
            showOTPForNewEmail: false,
            showFormForAddingEmail: false,
        }));
    }, [
        reset,
        user?.firstname,
        user?.lastname,
        user?.email,
        user?.phoneNo,
        newPhoneNo,
        phoneCode1,
        phoneCode2,
    ]);

    const handleCancelChangePhone = useCallback(() => {
        reset({
            firstname: user?.firstname,
            lastname: user?.lastname,
            email: user?.email,
            phoneNo: user?.phoneNo,
            newPhoneNo: '',
            phoneCode1: '',
            phoneCode2: '',
            newEmail,
            emailCode1,
            emailCode2,
        });
        setEditProfileState((prev) => ({
            ...prev,
            showOTPForNewPhone: false,
            showFormForAddingPhone: false,
            showOTPForExistingPhone: false,
        }));
    }, [
        reset,
        user?.firstname,
        user?.lastname,
        user?.email,
        user?.phoneNo,
        newEmail,
        emailCode1,
        emailCode2,
    ]);

    const handleAddPhoneNo = () => {
        setEditProfileState((prev) => ({
            ...prev,
            showFormForAddingPhone: true,
        }));
    };

    const handleGetOTP = async (params) => {
        const { type = 'email', newInput = false } = params || {};
        try {
            const payload = {
                type,
            };
            const editState = {
                type,
            };
            let startedOTP = '';

            if (type === 'phone') {
                editState.showOTPForExistingPhone = true;
                editState.showFormForAddingPhone = true;
                startedOTP = 'existingPhone';

                if (newInput) {
                    payload.phoneNo = newPhoneNo;
                    editState.showOTPForNewPhone = true;
                    startedOTP = 'newPhone';
                }
            }
            if (type === 'email') {
                if (newInput) {
                    editState.showOTPForNewEmail = true;
                    payload.email = newEmail;
                    startedOTP = 'newEmail';
                } else {
                    editState.showOTPForNewEmail = false;
                    editState.showFormForAddingEmail = true;
                    editState.showOTPforExistingEmail = true;
                    startedOTP = 'existingEmail';
                }
            }
            const res = await generateOTP(payload);
            if (!res?.status) throw new Error(res?.message);
            setEditProfileState((prev) => ({
                ...prev,
                ...editState,
            }));
            setStartCountdownForOTP((prev) => ({
                ...prev,
                [startedOTP]: true,
            }));
        } catch (error) {
            openNotification('error', { message: error?.message });
        }
    };

    const {
        showOTPforExistingEmail,
        showOTPForNewEmail,
        showOTPForExistingPhone,
        showOTPForNewPhone,
        showFormForAddingEmail,
        showFormForAddingPhone,
    } = editProfileState || {};

    const isEmailValid = useMemo(
        () => validateEmail(newEmail) && user?.email !== newEmail,
        [newEmail, user?.email]
    );
    const isPhoneNoValid = useMemo(
        () => validatePhoneNo(newPhoneNo) && user?.phoneNo !== newPhoneNo,
        [newPhoneNo, user?.phoneNo]
    );

    const isSaveEnabled = useMemo(() => {
        const isNameChanged =
            firstname !== user.firstname || lastname !== user.lastname;

        const isAddingPhone =
            showOTPForNewPhone &&
            !user?.phoneNo &&
            newPhoneNo &&
            isPhoneNoValid &&
            phoneCode2?.length === 6;

        const isChangingPhone =
            showOTPForExistingPhone &&
            showOTPForNewPhone &&
            user?.phoneNo &&
            newPhoneNo &&
            isPhoneNoValid &&
            phoneCode1?.length === 6 &&
            phoneCode2?.length === 6;

        const isChangingEmail =
            showOTPforExistingEmail &&
            showOTPForNewEmail &&
            newEmail &&
            isEmailValid &&
            newEmail !== user.email &&
            emailCode1?.length === 6 &&
            emailCode2?.length === 6;

        const isPhoneChanged = isAddingPhone || isChangingPhone;

        // Condition 1: If only the name changed, return true.
        if (isNameChanged && !isPhoneChanged && !isChangingEmail) {
            return true;
        }

        // Condition 2: If name, phone, and email all changed, return true only if all conditions are met.
        if (isNameChanged && isPhoneChanged && isChangingEmail) {
            return true;
        }

        // Condition 3: If only the phone changed, return true.
        if ((isNameChanged || isPhoneChanged) && !isChangingEmail) {
            return true;
        }

        // Condition 4: If only the email changed, return true.
        if ((isNameChanged || isChangingEmail) && !isPhoneChanged) {
            return true;
        }

        // Condition 5: If both phone and email changed, return true.
        if (
            (showOTPForExistingPhone || showOTPForNewPhone) &&
            showOTPforExistingEmail &&
            showOTPForNewEmail &&
            isPhoneChanged &&
            isChangingEmail
        ) {
            return true;
        }

        // If none of the specific conditions are met, the save button is not enabled.
        return false;
    }, [
        emailCode1,
        emailCode2,
        firstname,
        isEmailValid,
        isPhoneNoValid,
        lastname,
        newEmail,
        newPhoneNo,
        phoneCode1,
        phoneCode2,
        showOTPForExistingPhone,
        showOTPForNewEmail,
        showOTPForNewPhone,
        showOTPforExistingEmail,
        user.email,
        user.firstname,
        user.lastname,
        user?.phoneNo,
    ]);

    const showConfirmationUponCancel = useMemo(() => {
        return (
            isSaveEnabled ||
            Object.values(startCountdownForOTP).some((val) => val) ||
            Object.values(editProfileState).some((val) => val)
        );
    }, [isSaveEnabled, startCountdownForOTP, editProfileState]);

    return (
        <Modal
            visible={showUserProfileEdit}
            centered
            footer={null}
            className="tw-rounded-lg"
            bodyStyle={{
                borderRadius: 16,
            }}
            wrapClassName="tw-rounded-lg"
            maskClosable={false}
            keyboard={false}
            closeIcon={null}
            closable={false}
        >
            {!isEmpty(user) && (
                <>
                    <form
                        onSubmit={handleSubmit(handleFormSubmit)}
                        className="tw-flex tw-flex-col tw-gap-4 tw-p-4"
                    >
                        <h2 className="tw-flex tw-gap-1 tw-items-center">
                            <span className="tw-text-base tw-text-gray-400">
                                {user?.firstname && user?.lastname
                                    ? `${user?.firstname} ${user?.lastname}`
                                    : user?.firstname || user?.lastname}{' '}
                            </span>
                            / Edit profile
                        </h2>

                        {/* First Name */}
                        <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-overflow-hidden tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                            <label htmlFor="firstname" className="sr-only">
                                First Name
                            </label>
                            <input
                                defaultValue={user?.firstname}
                                id="firstname"
                                name="firstname"
                                ref={register('firstname', {
                                    required: {
                                        value: true,
                                        message: 'First Name is required',
                                    },
                                    pattern: {
                                        value: /^[a-zA-Z0-9'-]*$/,
                                        message: 'Enter valid first name.',
                                    },
                                })}
                                className="tw-p-2 tw-w-full tw-border-none"
                                placeholder="First Name"
                                onChange={(event) =>
                                    setValue('firstname', event.target.value)
                                }
                            />
                        </div>

                        {/* Last Name */}
                        <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-overflow-hidden tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                            <label htmlFor="lastname" className="sr-only">
                                Last Name
                            </label>
                            <input
                                defaultValue={user?.lastname}
                                id="lastname"
                                name="lastname"
                                ref={register('lastname', {
                                    required: {
                                        value: true,
                                        message: 'Last Name is required',
                                    },
                                    pattern: {
                                        value: /^[a-zA-Z0-9'-]*$/,
                                        message: 'Enter valid last name.',
                                    },
                                })}
                                onChange={(event) =>
                                    setValue('lastname', event.target.value)
                                }
                                className="tw-p-2 tw-w-full tw-border-none"
                                placeholder="Last Name"
                            />
                        </div>
                        {!user?.phoneNo && (
                            <>
                                {!showFormForAddingPhone && (
                                    <Button
                                        type="dashed"
                                        onClick={handleAddPhoneNo}
                                        block
                                        icon={<PlusOutlined />}
                                    >
                                        Add Phone no.
                                    </Button>
                                )}
                                {showFormForAddingPhone && (
                                    <>
                                        <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                                            <input
                                                hidden
                                                id="newPhoneNo"
                                                name="newPhoneNo"
                                                ref={register('newPhoneNo')}
                                                className="tw-p-2 tw-w-full"
                                                placeholder="New email"
                                                onChange={(event) =>
                                                    setValue(
                                                        'newPhoneNo',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                            <PhoneInput
                                                name="newPhoneNo"
                                                country="sg"
                                                enableSearch
                                                inputStyle={{
                                                    width: '100%',
                                                }}
                                                isValid={(
                                                    inputNumber,
                                                    country,
                                                    countries
                                                ) => {
                                                    return countries.some(
                                                        (country) => {
                                                            return (
                                                                startsWith(
                                                                    inputNumber,
                                                                    country.dialCode
                                                                ) ||
                                                                startsWith(
                                                                    country.dialCode,
                                                                    inputNumber
                                                                )
                                                            );
                                                        }
                                                    );
                                                }}
                                                onChange={(
                                                    value,
                                                    data,
                                                    _,
                                                    formattedValue
                                                ) => {
                                                    setValue(
                                                        'newPhoneNo',
                                                        formattedValue.replaceAll(
                                                            '-',
                                                            ''
                                                        )
                                                    );
                                                }}
                                            />
                                        </div>

                                        {phoneCode2 && !isPhoneNoValid && (
                                            <span className="tw-text-rose-600 tw-text-xxs tw-flex tw-w-full">
                                                Phone is invalid or the same
                                                with the existing
                                            </span>
                                        )}
                                        <div className="cta tw-w-full tw-flex tw-flex-1 tw-items-center tw-gap-2">
                                            <Button
                                                htmlType="button"
                                                type="default"
                                                size="small"
                                                onClick={
                                                    handleCancelChangePhone
                                                }
                                                className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                htmlType="button"
                                                type="primary"
                                                size="small"
                                                className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                                disabled={
                                                    !newPhoneNo ||
                                                    startCountdownForOTP.newPhone
                                                }
                                                onClick={() =>
                                                    handleGetOTP({
                                                        type: 'phone',
                                                        newInput: true,
                                                    })
                                                }
                                            >
                                                Get OTP
                                            </Button>

                                            {startCountdownForOTP.newPhone && (
                                                <Tooltip title="Time to request OTP">
                                                    <CountDownTimer
                                                        timeLimit={
                                                            REQUEST_OTP_TIME
                                                        }
                                                        onEnd={() =>
                                                            setStartCountdownForOTP(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    newPhone: false,
                                                                })
                                                            )
                                                        }
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                        {showOTPForNewPhone && (
                                            <>
                                                <input
                                                    hidden
                                                    id="phoneCode2"
                                                    name="phoneCode2"
                                                    ref={register(
                                                        'phoneCode2',
                                                        {
                                                            required: {
                                                                value: true,
                                                                message:
                                                                    'Email otp is required',
                                                            },
                                                            maxLength: {
                                                                value: 6,
                                                                message:
                                                                    'Maximum OTP is 6',
                                                            },
                                                            minLength: {
                                                                value: 6,
                                                                message:
                                                                    'Minimum OTP is 6',
                                                            },
                                                        }
                                                    )}
                                                    className="tw-p-2 tw-w-full"
                                                    placeholder="Email"
                                                />
                                                <div className="tw-flex tw-items-center tw-w-full">
                                                    <OTPInput
                                                        value={phoneCode2}
                                                        inputStyles={
                                                            inputStyles
                                                        }
                                                        className="tw-font-bold tw-text-lg"
                                                        onChange={(inputNo) => {
                                                            setValue(
                                                                'phoneCode2',
                                                                inputNo
                                                            );
                                                        }}
                                                        autoFocus
                                                        OTPLength={6}
                                                        otpType="number"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}

                        {user?.phoneNo && (
                            <>
                                <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-overflow-hidden tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                                    <label
                                        htmlFor="phoneNo"
                                        className="sr-only"
                                    >
                                        Phone No.
                                    </label>
                                    <input
                                        value={user?.phoneNo}
                                        id="phoneNo"
                                        ref={register('phoneNo')}
                                        disabled
                                        className="tw-w-full tw-h-full tw-p-2 disabled:tw-opacity-60 border-none"
                                        placeholder="Phone no."
                                    />

                                    <Tooltip
                                        title={
                                            startCountdownForOTP?.existingPhone
                                                ? 'Time to request OTP'
                                                : 'Edit Phone no.'
                                        }
                                    >
                                        {startCountdownForOTP?.existingPhone ? (
                                            <span className="tw-inline-flex tw-px-1">
                                                <CountDownTimer
                                                    timeLimit={REQUEST_OTP_TIME}
                                                    onEnd={() =>
                                                        setStartCountdownForOTP(
                                                            (prev) => ({
                                                                ...prev,
                                                                existingPhone: false,
                                                            })
                                                        )
                                                    }
                                                />
                                            </span>
                                        ) : (
                                            <Button
                                                className="tw-border-none tw-border-l tw-border-[#d9d9d9]"
                                                htmlType="button"
                                                icon={<EditOutlined />}
                                                onClick={() =>
                                                    handleGetOTP({
                                                        type: 'phone',
                                                    })
                                                }
                                                disabled={
                                                    startCountdownForOTP?.existingPhone
                                                }
                                            />
                                        )}
                                    </Tooltip>
                                </div>

                                <div className="tw-flex tw-pl-3 tw-border-l tw-flex-col tw-justify-center tw-flex-1 tw-items-center empty:tw-hidden tw-border-none tw-gap-4">
                                    {showOTPForExistingPhone && (
                                        <>
                                            <input
                                                hidden
                                                id=""
                                                name="phoneCode1"
                                                ref={register('phoneCode1', {
                                                    required: {
                                                        value: true,
                                                        message:
                                                            'Email otp is required',
                                                    },
                                                    maxLength: {
                                                        value: 6,
                                                        message:
                                                            'Maximum OTP is 6',
                                                    },
                                                    minLength: {
                                                        value: 6,
                                                        message:
                                                            'Minimum OTP is 6',
                                                    },
                                                })}
                                                className="tw-p-2 tw-w-full"
                                                placeholder="Email"
                                            />
                                            <div className="tw-flex tw-items-center tw-w-full">
                                                <OTPInput
                                                    value={phoneCode1}
                                                    inputStyles={inputStyles}
                                                    className="tw-font-bold tw-text-lg"
                                                    onChange={(inputNo) => {
                                                        setValue(
                                                            'phoneCode1',
                                                            inputNo
                                                        );
                                                    }}
                                                    autoFocus
                                                    OTPLength={6}
                                                    otpType="number"
                                                />
                                            </div>
                                        </>
                                    )}
                                    {showFormForAddingPhone &&
                                        phoneCode1?.length === 6 && (
                                            <>
                                                <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                                                    <input
                                                        hidden
                                                        id="newPhoneNo"
                                                        name="newPhoneNo"
                                                        ref={register(
                                                            'newPhoneNo'
                                                        )}
                                                        className="tw-p-2 tw-w-full"
                                                        placeholder="New email"
                                                        onChange={(event) =>
                                                            setValue(
                                                                'newPhoneNo',
                                                                event.target
                                                                    .value
                                                            )
                                                        }
                                                    />
                                                    <PhoneInput
                                                        name="newPhoneNo"
                                                        country="sg"
                                                        enableSearch
                                                        inputStyle={{
                                                            width: '100%',
                                                        }}
                                                        isValid={(
                                                            inputNumber,
                                                            country,
                                                            countries
                                                        ) => {
                                                            return countries.some(
                                                                (country) => {
                                                                    return (
                                                                        startsWith(
                                                                            inputNumber,
                                                                            country.dialCode
                                                                        ) ||
                                                                        startsWith(
                                                                            country.dialCode,
                                                                            inputNumber
                                                                        )
                                                                    );
                                                                }
                                                            );
                                                        }}
                                                        onChange={(
                                                            value,
                                                            data,
                                                            _,
                                                            formattedValue
                                                        ) => {
                                                            setValue(
                                                                'newPhoneNo',
                                                                formattedValue.replaceAll(
                                                                    '-',
                                                                    ''
                                                                )
                                                            );
                                                        }}
                                                    />
                                                </div>
                                                {phoneCode2 &&
                                                    !isPhoneNoValid && (
                                                        <span className="tw-text-rose-600 tw-text-xxs tw-flex tw-w-full">
                                                            Phone is invalid or
                                                            the same with the
                                                            existing
                                                        </span>
                                                    )}
                                                <div className="cta tw-w-full tw-flex tw-flex-1 tw-items-center tw-gap-2">
                                                    <Button
                                                        htmlType="button"
                                                        type="default"
                                                        size="small"
                                                        onClick={
                                                            handleCancelChangePhone
                                                        }
                                                        className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        htmlType="button"
                                                        type="primary"
                                                        size="small"
                                                        className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                                        disabled={
                                                            !newPhoneNo ||
                                                            startCountdownForOTP.newPhone
                                                        }
                                                        onClick={() =>
                                                            handleGetOTP({
                                                                type: 'phone',
                                                                newInput: true,
                                                            })
                                                        }
                                                    >
                                                        Get OTP
                                                    </Button>
                                                    {startCountdownForOTP.newPhone && (
                                                        <Tooltip title="Time to request OTP">
                                                            <CountDownTimer
                                                                timeLimit={
                                                                    REQUEST_OTP_TIME
                                                                }
                                                                onEnd={() =>
                                                                    setStartCountdownForOTP(
                                                                        (
                                                                            prev
                                                                        ) => ({
                                                                            ...prev,
                                                                            newPhone: false,
                                                                        })
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    {showOTPForNewPhone && (
                                        <>
                                            <input
                                                hidden
                                                id="phoneCode2"
                                                name="phoneCode2"
                                                ref={register('phoneCode2', {
                                                    required: {
                                                        value: true,
                                                        message:
                                                            'Email otp is required',
                                                    },
                                                    maxLength: {
                                                        value: 6,
                                                        message:
                                                            'Maximum OTP is 6',
                                                    },
                                                    minLength: {
                                                        value: 6,
                                                        message:
                                                            'Minimum OTP is 6',
                                                    },
                                                })}
                                                className="tw-p-2 tw-w-full"
                                                placeholder="Email"
                                            />
                                            <div className="tw-flex tw-items-center tw-w-full">
                                                <OTPInput
                                                    value={phoneCode2}
                                                    inputStyles={inputStyles}
                                                    className="tw-font-bold tw-text-lg"
                                                    onChange={(inputNo) => {
                                                        setValue(
                                                            'phoneCode2',
                                                            inputNo
                                                        );
                                                    }}
                                                    autoFocus
                                                    OTPLength={6}
                                                    otpType="number"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Email */}
                        <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-overflow-hidden tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                            <label htmlFor="email" className="sr-only">
                                Email
                            </label>
                            <input
                                value={user?.email}
                                id="email"
                                name="email"
                                ref={register('email', { required: true })}
                                className="tw-w-full tw-h-full tw-p-2 disabled:tw-opacity-60"
                                disabled
                            />
                            <Tooltip
                                title={
                                    startCountdownForOTP?.existingEmail
                                        ? 'Time to request OTP'
                                        : 'Edit email'
                                }
                            >
                                {startCountdownForOTP?.existingEmail ? (
                                    <span className="tw-inline-flex tw-px-1">
                                        <CountDownTimer
                                            timeLimit={REQUEST_OTP_TIME}
                                            onEnd={() =>
                                                setStartCountdownForOTP(
                                                    (prev) => ({
                                                        ...prev,
                                                        existingEmail: false,
                                                    })
                                                )
                                            }
                                        />
                                    </span>
                                ) : (
                                    <Button
                                        className="tw-border-none tw-border-l tw-border-[#d9d9d9]"
                                        htmlType="button"
                                        icon={<EditOutlined />}
                                        onClick={() =>
                                            handleGetOTP({
                                                type: 'email',
                                            })
                                        }
                                        disabled={
                                            startCountdownForOTP?.existingEmail
                                        }
                                    />
                                )}
                            </Tooltip>
                        </div>
                        <div className="tw-flex tw-pl-3 tw-border-l tw-flex-col tw-justify-center tw-flex-1 tw-items-center empty:tw-hidden tw-border-none tw-gap-4">
                            {showOTPforExistingEmail && (
                                <>
                                    <input
                                        hidden
                                        id=""
                                        name="emailCode1"
                                        ref={register('emailCode1', {
                                            required: {
                                                value: true,
                                                message:
                                                    'Email otp is required',
                                            },
                                            maxLength: {
                                                value: 6,
                                                message: 'Maximum OTP is 6',
                                            },
                                            minLength: {
                                                value: 6,
                                                message: 'Minimum OTP is 6',
                                            },
                                        })}
                                        className="tw-p-2 tw-w-full"
                                        placeholder="Email"
                                    />
                                    <div className="tw-flex tw-items-center tw-w-full">
                                        <OTPInput
                                            value={emailCode1}
                                            inputStyles={inputStyles}
                                            className="tw-font-bold tw-text-lg"
                                            onChange={(inputNo) => {
                                                setValue('emailCode1', inputNo);
                                            }}
                                            autoFocus
                                            OTPLength={6}
                                            otpType="number"
                                        />
                                    </div>
                                </>
                            )}
                            {showFormForAddingEmail &&
                                emailCode1?.length === 6 && (
                                    <>
                                        <div className="tw-flex tw-w-full tw-items-center tw-rounded-md tw-border-solid tw-border-[1px] tw-border-[#d9d9d9]">
                                            <input
                                                id="newEmail"
                                                name="newEmail"
                                                ref={register('newEmail', {
                                                    pattern: {
                                                        value: /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/,
                                                        message: `New email is invalid`,
                                                    },
                                                    required: {
                                                        value: true,
                                                        message:
                                                            'Email is required',
                                                    },
                                                })}
                                                className="tw-p-2 tw-w-full"
                                                placeholder="New email"
                                                onChange={(event) =>
                                                    setValue(
                                                        'newEmail',
                                                        event.target.value
                                                    )
                                                }
                                            />
                                        </div>
                                        {newEmail && !isEmailValid && (
                                            <span className="tw-text-rose-600 tw-text-xxs tw-flex tw-w-full">
                                                Email is invalid or the same
                                                with the existing
                                            </span>
                                        )}
                                        <div className="cta tw-w-full tw-flex tw-flex-1 tw-items-center tw-gap-2">
                                            <Button
                                                htmlType="button"
                                                type="default"
                                                size="small"
                                                onClick={
                                                    handleCancelChangeEmail
                                                }
                                                className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                htmlType="button"
                                                type="primary"
                                                size="small"
                                                className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                                disabled={
                                                    !isEmailValid ||
                                                    startCountdownForOTP.newEmail
                                                }
                                                onClick={() =>
                                                    handleGetOTP({
                                                        type: 'email',
                                                        newInput: true,
                                                    })
                                                }
                                            >
                                                Get OTP
                                            </Button>
                                            {startCountdownForOTP.newEmail && (
                                                <Tooltip title="Time to request OTP">
                                                    <CountDownTimer
                                                        timeLimit={
                                                            REQUEST_OTP_TIME
                                                        }
                                                        onEnd={() =>
                                                            setStartCountdownForOTP(
                                                                (prev) => ({
                                                                    ...prev,
                                                                    newEmail: false,
                                                                })
                                                            )
                                                        }
                                                    />
                                                </Tooltip>
                                            )}
                                        </div>
                                    </>
                                )}
                            {showOTPForNewEmail && (
                                <>
                                    <input
                                        hidden
                                        id="emailCode2"
                                        name="emailCode2"
                                        ref={register('emailCode2', {
                                            required: {
                                                value: true,
                                                message:
                                                    'Email otp is required',
                                            },
                                            maxLength: {
                                                value: 6,
                                                message: 'Maximum OTP is 6',
                                            },
                                            minLength: {
                                                value: 6,
                                                message: 'Minimum OTP is 6',
                                            },
                                        })}
                                        className="tw-p-2 tw-w-full"
                                        placeholder="Email"
                                    />
                                    <div className="tw-flex tw-items-center tw-w-full">
                                        <OTPInput
                                            value={emailCode2}
                                            inputStyles={inputStyles}
                                            className="tw-font-bold tw-text-lg"
                                            onChange={(inputNo) => {
                                                setValue('emailCode2', inputNo);
                                            }}
                                            autoFocus
                                            OTPLength={6}
                                            otpType="number"
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="tw-flex tw-w-full tw-gap-2">
                            {showConfirmationUponCancel ? (
                                <Popconfirm
                                    title="Are you sure you want to discard changes?"
                                    onConfirm={handleCancel}
                                >
                                    <Button
                                        htmlType="button"
                                        type="default"
                                        className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                    >
                                        Cancel
                                    </Button>
                                </Popconfirm>
                            ) : (
                                <Button
                                    htmlType="button"
                                    type="default"
                                    onClick={handleCancel}
                                    className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                >
                                    Cancel
                                </Button>
                            )}
                            <Button
                                htmlType="submit"
                                type="primary"
                                className="tw-w-full tw-rounded-md tw-leading-[0] tw-p-1"
                                disabled={!isSaveEnabled}
                                loading={submitting}
                            >
                                Save changes
                            </Button>
                        </div>
                    </form>
                    <style>{`
                #project-dashboard-header .ant-modal-content {
                    border-radius: 15px;
                }
            `}</style>
                </>
            )}
        </Modal>
    );
};

export default UserProfileEdit;
